import { useEffect, useRef, useState } from "react";
import { Menu, Search, Settings, LogOut, X } from "lucide-react";
import { GlobalSearch } from "./GlobalSearch";
import { pokemonSpriteUrl } from "../lib/images";
import { eur } from "../lib/prices";

// Barre du haut, « Mix 2 · Éditorial lumineux » choisi par l'utilisateur le
// 2026-10-03 (maquette des trois mix) : DEUX niveaux.
//   - Ligne 1 : marque (nom en dégradé blanc → accent), recherche TOUJOURS
//     visible sur grand écran, pastille avatar (Pokémon choisi dans Réglages,
//     sinon l'initiale) qui ouvre le menu (entrées secondaires, Réglages,
//     Déconnexion).
//   - Ligne 2 (grand écran) : rubriques en capitales, sans icône, trait
//     épais lumineux sous la rubrique active ; à droite trois chiffres de la
//     collection (cartes possédées, collections suivies, valeur estimée).
//   Halos très doux (accent à gauche, bleu à droite) sur toute la barre.
// Sur mobile : ligne 1 seule (menu = tiroir, marque, loupe qui ouvre un
// champ pleine largeur sous la barre) ; les rubriques vivent dans le tiroir.
// La hauteur de la barre est partagée avec la barre latérale via la variable
// CSS `--topnav-h` (App.css) : si on change les lignes, changer la variable.
export const TopNav = ({ tabs, user, avatar, menuItems = [], stats, onHome, onMenu, onSettings, onLogout, searchHandlers }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => setAvatarFailed(false), [avatar]);

  // « / » met le focus dans la recherche (hors champ de saisie).
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        const input = document.querySelector(".topnav-search input");
        if (input) input.focus(); else setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  const initial = (user?.username || "?").slice(0, 1).toUpperCase();
  const showSprite = avatar && !avatarFailed;

  return (
    <header className="header-glass topnav">
      {/* Les halos vivent dans un calque à part qui, LUI, est en overflow
          hidden — jamais la barre elle-même, sinon la liste déroulante de la
          recherche (qui dépasse de la barre) est coupée (retour utilisateur
          du 2026-10-11 : suggestions invisibles sur le site et dans l'appli). */}
      <span className="topnav-bg" aria-hidden="true">
        <span className="topnav-halo topnav-halo-a" />
        <span className="topnav-halo topnav-halo-b" />
      </span>

      <div className="topnav-row1">
        {/* Enveloppes `lg:hidden` séparées : `.topnav-iconbtn` pose son propre
            display et gagnerait sur l'utilitaire Tailwind s'il était sur le
            même élément (même spécificité, App.css chargé après). */}
        <div className="lg:hidden flex items-center">
          <button className="topnav-iconbtn" title="Menu" onClick={onMenu}>
            <Menu className="w-5 h-5" />
          </button>
        </div>
        <button className="topnav-brand" onClick={onHome}>
          <img src="/bulbizarbi-icon.png" alt="" className="w-10 h-10 object-contain shrink-0" />
          <span className="min-w-0 text-left">
            <span className="topnav-brand-name">Bulbizarbi</span>
            <span className="topnav-brand-sub hidden sm:block">Collection TCG</span>
          </span>
        </button>

        <div className="topnav-right">
          {/* Grand écran : champ de recherche toujours visible. */}
          <div className="hidden lg:block topnav-search">
            <GlobalSearch {...searchHandlers} />
          </div>
          {/* Mobile : loupe → rangée pleine largeur sous la barre. */}
          <div className="lg:hidden flex items-center">
            <button className="topnav-iconbtn" title="Rechercher" onClick={() => setSearchOpen(v => !v)}>
              {searchOpen ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
            </button>
          </div>

          <div className="relative hidden lg:block" ref={menuRef}>
            <button className={`topnav-avatar ${menuOpen ? "is-open" : ""}`} onClick={() => setMenuOpen(v => !v)} title={user?.username}>
              {showSprite ? (
                <span className="topnav-avatar-sprite">
                  <img src={pokemonSpriteUrl(avatar)} alt="" onError={() => setAvatarFailed(true)} />
                </span>
              ) : (
                <span className="topnav-avatar-initial">{initial}</span>
              )}
              <span className="topnav-avatar-name">{user?.username}</span>
            </button>
            {menuOpen && (
              <div className="topnav-menu">
                {menuItems.map(({ key, label, icon: Icon, onClick }) => (
                  <button key={key} onClick={() => { setMenuOpen(false); onClick(); }}>
                    {Icon && <Icon className="w-4 h-4" />}{label}
                  </button>
                ))}
                {menuItems.length > 0 && <div className="topnav-menu-sep" />}
                <button onClick={() => { setMenuOpen(false); onSettings(); }}><Settings className="w-4 h-4" />Réglages</button>
                <button onClick={() => { setMenuOpen(false); onLogout(); }}><LogOut className="w-4 h-4" />Se déconnecter</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="topnav-row2 hidden lg:flex">
        <nav className="topnav-tabs" aria-label="Navigation principale">
          {tabs.map(({ key, label, active, onClick, tone }) => (
            <button key={key} onClick={onClick}
              className={`topnav-tab ${active ? "is-active" : ""} ${tone === "admin" ? "is-admin" : ""}`}
              aria-current={active ? "page" : undefined}>
              {label}
            </button>
          ))}
        </nav>
        {stats && (
          <div className="topnav-meta">
            <span><b>{(stats.cards ?? 0).toLocaleString("fr-FR")}</b> carte{stats.cards > 1 ? "s" : ""}</span>
            <span><b>{stats.sets ?? 0}</b> collection{stats.sets > 1 ? "s" : ""}</span>
            {stats.value > 0 && <span><b>{eur(stats.value)}</b></span>}
          </div>
        )}
      </div>

      {searchOpen && <div className="topnav-search-row lg:hidden"><GlobalSearch {...searchHandlers} autoFocus onClose={() => setSearchOpen(false)} wide /></div>}
    </header>
  );
};
